/**
 * particles.js — Background starfield + golden mouse dust
 *
 * initParticles(scene, loader) → { animateParticles, spawnDust, raycaster, mouseCoords }
 *
 * animateParticles(elapsedTime) — call every frame from the render loop
 * spawnDust(normX, normY, camera) — call from mousemove/touchmove handlers
 * raycaster, mouseCoords — exported so cards.js can reuse them for hit testing
 */

import * as THREE from 'three'

export function initParticles(scene, loader) {

    // ── Shared texture ─────────────────────────────────────────
    const particlesTexture = loader.load('./particles/1.png')
    particlesTexture.colorSpace = THREE.SRGBColorSpace

    // ── Background starfield ───────────────────────────────────
    const PARTICLE_COUNT = 500
    const positions  = new Float32Array(PARTICLE_COUNT * 3)
    const colors     = new Float32Array(PARTICLE_COUNT * 3)

    for (let i = 0; i < PARTICLE_COUNT; i++) {
        positions[i * 3]     = (Math.random() - 0.5) * 10
        positions[i * 3 + 1] = (Math.random() - 0.5) * 10
        positions[i * 3 + 2] = (Math.random() - 0.5) * 10
        colors[i * 3]     = Math.random()
        colors[i * 3 + 1] = Math.random()
        colors[i * 3 + 2] = Math.random()
    }

    const baseColors = new Float32Array(colors)

    const particlesGeo = new THREE.BufferGeometry()
    particlesGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    particlesGeo.setAttribute('color',    new THREE.BufferAttribute(colors, 3))

    const particlesMat = new THREE.PointsMaterial({
        size: 0.3,
        sizeAttenuation: true,
        color: new THREE.Color('rgba(253, 233, 51, 1)'),
        vertexColors: true,
    })
    particlesMat.map        = particlesTexture
    particlesMat.transparent = true
    particlesMat.alphaMap   = particlesTexture
    particlesMat.depthWrite = false
    particlesMat.alphaTest  = 0.001
    particlesMat.blending   = THREE.AdditiveBlending

    const particles = new THREE.Points(particlesGeo, particlesMat)
    scene.add(particles)

    // ── Golden mouse dust ──────────────────────────────────────
    const DUST_COUNT   = 100
    const dustPositions = new Float32Array(DUST_COUNT * 3)
    const dustAlpha     = new Float32Array(DUST_COUNT)

    const dustGeo = new THREE.BufferGeometry()
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3))
    dustGeo.setAttribute('alpha',    new THREE.BufferAttribute(dustAlpha, 1))

    const dustMat = new THREE.ShaderMaterial({
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        uniforms: {
            uTime:    { value: 0 },
            uTexture: { value: particlesTexture },
        },
        vertexShader: `
            attribute float alpha;
            varying float vAlpha;
            void main() {
                vAlpha = alpha;
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                gl_PointSize = 40.0 * (1.0 / -mvPosition.z);
                gl_Position = projectionMatrix * mvPosition;
            }
        `,
        fragmentShader: `
            uniform sampler2D uTexture;
            varying float vAlpha;
            void main() {
                vec4 tex = texture2D(uTexture, gl_PointCoord);
                gl_FragColor = vec4(1.0, 0.85, 0.3, tex.a * vAlpha);
            }
        `,
    })

    const mouseDust = new THREE.Points(dustGeo, dustMat)
    scene.add(mouseDust)

    // ── Mouse / raycaster state ────────────────────────────────
    // Exported so cards.js can reuse them for hit testing
    const raycaster   = new THREE.Raycaster()
    const mouseCoords = new THREE.Vector2()
    const mouseThree  = new THREE.Vector3()
    let dustIndex = 0

    // ── spawnDust ──────────────────────────────────────────────
    // Call from mousemove/touchmove with normalised coords (-1..1)
    function spawnDust(normX, normY, camera) {
        mouseCoords.x = normX
        mouseCoords.y = normY

        const vector = new THREE.Vector3(normX, normY, 0.5)
        vector.unproject(camera)
        const dir      = vector.sub(camera.position).normalize()
        const distance = -camera.position.z / dir.z + 3
        mouseThree.copy(camera.position).add(dir.multiplyScalar(distance))

        const i = dustIndex * 3
        dustPositions[i]     = mouseThree.x + (Math.random() - 0.5) * 0.2
        dustPositions[i + 1] = mouseThree.y + (Math.random() - 0.5) * 0.2
        dustPositions[i + 2] = mouseThree.z + (Math.random() - 0.5) * 0.2
        dustAlpha[dustIndex]  = 1.0
        dustIndex = (dustIndex + 1) % DUST_COUNT
    }

    // ── animateParticles ───────────────────────────────────────
    // Call every frame with elapsedTime in seconds
    function animateParticles(elapsedTime) {
        // Starfield flicker
        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3
            const flicker = 0.5 + 0.5 * Math.sin(elapsedTime * 3 + i)
            colors[i3]     = baseColors[i3]     * flicker
            colors[i3 + 1] = baseColors[i3 + 1] * flicker
            colors[i3 + 2] = baseColors[i3 + 2] * flicker
        }
        particlesGeo.attributes.color.needsUpdate = true

        // Slow rotation + sine-wave Y drift
        particles.rotation.y = elapsedTime * 0.02
        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3
            const x = particlesGeo.attributes.position.array[i3]
            const y = particlesGeo.attributes.position.array[i3 + 1]
            const z = particlesGeo.attributes.position.array[i3 + 2]
            particlesGeo.attributes.position.array[i3 + 1] =
                y + Math.sin(elapsedTime + x * 2 + z * 2) * 0.0005
        }
        particlesGeo.attributes.position.needsUpdate = true

        // Dust fade + gentle drift
        const pos    = dustGeo.attributes.position.array
        const alphas = dustGeo.attributes.alpha.array
        for (let i = 0; i < DUST_COUNT; i++) {
            if (alphas[i] > 0) {
                alphas[i]       -= 0.015
                pos[i * 3 + 1]  -= 0.005
                pos[i * 3]      += Math.sin(elapsedTime + i) * 0.002
            }
        }
        dustGeo.attributes.position.needsUpdate = true
        dustGeo.attributes.alpha.needsUpdate    = true
    }

    function destroy() {
        scene.remove(particles)
        scene.remove(mouseDust)
        particlesGeo.dispose()
        dustGeo.dispose()
        particlesMat.dispose()
        dustMat.dispose()
        particlesTexture.dispose()
    }

    return { animateParticles, spawnDust, raycaster, mouseCoords, destroy }
}
